import { toast } from "@/components/ToastNotification";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { useMutation } from "@tanstack/react-query";
import { Eye, Save } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Anexos from "../components/anexos";
import ExamesSolicitacao from "../components/examesSolicitacao";
import { StatusSelector } from "../components/StatusSelector";
import VisualizadorArquivo from "../components/visualizadorArquivo";
import { StatusAtendimento } from "../constants/statusAtendimento";
import { Exame, ProntuarioRetinologoProps } from "../interfaces/prontuarioRetinologo.interface";
import { ProntuarioRetinologoFormData, getInitialData } from "../models/prontuarioRetinologo.model";
import { buscarPdfParaVisualizacao } from "../utils/fetchPdfParaVisualizacao";
import { saveFormProtocolo } from "../utils/formSaveHelper";
import { CampoObrigatorio, validarCamposObrigatorios } from "../utils/formValidation";

export const ProntuarioRetinologo = ({ onDataChange, initialData, atendimentoId, pacienteId, empresaId }: ProntuarioRetinologoProps) => {
  const [formData, setFormData] = useState<ProntuarioRetinologoFormData>(() => {
    const initialFormData = getInitialData(initialData);
    // Se não houver status_atendimento nos dados iniciais, definir como 'Novo'
    if (!initialFormData.status_atendimento) {
      return { ...initialFormData, status_atendimento: StatusAtendimento.NOVO };
    }
    return initialFormData;
  });
  const [statusAtendimento, setStatusAtendimento] = useState<string | null>(() => {
    const initialFormData = getInitialData(initialData);
    return initialFormData.status_atendimento || StatusAtendimento.NOVO;
  });
  const [protocoloId, setProtocoloId] = useState<number | undefined>(initialData?.id);
  const [quantidadeExames, setQuantidadeExames] = useState<number>(0);

  const [availableExams, setAvailableExams] = useState<Exame[]>([]);
  const [loadingExams, setLoadingExams] = useState(false);
  const [isExamDialogOpen, setIsExamDialogOpen] = useState(false);
  const [tempSelectedExams, setTempSelectedExams] = useState<string[]>([]);
  const [visualizadorAberto, setVisualizadorAberto] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);

  const fetchExames = useCallback(async () => {
    try {
      setLoadingExams(true);
      const response: { result: Exame[] | { data: Exame[] } } = await api.get(`/api/exames`);
      const exams = Array.isArray(response.result) 
        ? response.result 
        : (response.result as any)?.data || [];
      setAvailableExams(exams);
    } catch (error) {
      console.error("Erro ao carregar exames:", error);
      setAvailableExams([]);
    } finally {
      setLoadingExams(false);
    }
  }, []);

  useEffect(() => {
    fetchExames();
  }, [fetchExames]);

  // Função auxiliar para extrair exames da resposta
  const extractExamesFromResponse = useCallback((data: any): string[] => {
    if (!data) return [];
    
    // Se já vier como array de strings
    if (Array.isArray(data.examesNecessarios)) {
      return data.examesNecessarios;
    }
    
    // Se vier como array de objetos examesSolicitados
    if (Array.isArray(data.examesSolicitados)) {
      return data.examesSolicitados
        .map((es: any) => es.exame?.nome || es.nome)
        .filter((nome: string) => nome);
    }
    
    return [];
  }, []);

  // Atualizar formData quando initialData mudar (ex: após carregar dados do backend)
  useEffect(() => {
    if (initialData) {
      console.log('🔄 [ProntuarioRetinologo] Atualizando formData com initialData:', {
        id: initialData.id,
        hasExamesNecessarios: !!initialData.examesNecessarios,
        examesCount: initialData.examesNecessarios?.length || 0,
        exames: initialData.examesNecessarios
      });
      
      const updatedData = getInitialData(initialData);
      
      // Extrair exames se vierem no formato examesSolicitados
      if (!updatedData.examesNecessarios || updatedData.examesNecessarios.length === 0) {
        const exames = extractExamesFromResponse(initialData);
        if (exames.length > 0) {
          updatedData.examesNecessarios = exames;
          setQuantidadeExames(exames.length);
        }
      } else {
        setQuantidadeExames(updatedData.examesNecessarios.length);
      }
      
      // Se não houver status_atendimento nos dados iniciais, definir como 'Novo'
      if (!updatedData.status_atendimento) {
        updatedData.status_atendimento = StatusAtendimento.NOVO;
      }
      setFormData(updatedData);
      if (initialData.status_atendimento) {
        setStatusAtendimento(initialData.status_atendimento);
      } else {
        setStatusAtendimento(StatusAtendimento.NOVO);
      }
      if (initialData.id) {
        setProtocoloId(initialData.id);
      }
    }
  }, [initialData, extractExamesFromResponse]);

  // Carregar exames quando protocoloId estiver disponível
  useEffect(() => {
    const loadExames = async () => {
      if (!protocoloId) return;
      
      // Verificar se já temos exames no formData
      const currentExames = formData.examesNecessarios;
      if (currentExames && currentExames.length > 0) {
        console.log('ℹ️ [ProntuarioRetinologo] Exames já carregados no formData:', currentExames);
        return;
      }
      
      try {
        console.log('🔍 [ProntuarioRetinologo] Carregando exames para protocolo:', protocoloId);
        const response = await api.get(`/api/protocolo-prontuario-retinologo/${protocoloId}`);
        
        if (response?.result) {
          const exames = extractExamesFromResponse(response.result);
          if (exames && exames.length > 0) {
            console.log('✅ [ProntuarioRetinologo] Exames carregados do backend:', exames);
            setFormData((prevData) => ({
              ...prevData,
              examesNecessarios: exames
            }));
            setQuantidadeExames(exames.length);
          } else {
            console.log('ℹ️ [ProntuarioRetinologo] Nenhum exame encontrado para este protocolo');
            setQuantidadeExames(0);
          }
        }
      } catch (error) {
        console.error('❌ [ProntuarioRetinologo] Erro ao carregar exames:', error);
        setQuantidadeExames(0);
      }
    };

    loadExames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocoloId, extractExamesFromResponse]);

  // Atualizar quantidadeExames quando formData.examesNecessarios mudar
  useEffect(() => {
    const examesCount = Array.isArray(formData.examesNecessarios) ? formData.examesNecessarios.length : 0;
    if (examesCount > 0 && examesCount !== quantidadeExames) {
      console.log('🔄 [ProntuarioRetinologo] Atualizando quantidadeExames:', examesCount);
      setQuantidadeExames(examesCount);
    }
  }, [formData.examesNecessarios, quantidadeExames]);

  // Mutation para salvar o formulário
  const saveMutation = useMutation({
    mutationFn: async (data: ProntuarioRetinologoFormData) => {
      // Extrair examesNecessarios antes de salvar o protocolo
      const examesNecessarios = data.examesNecessarios || [];
      data.id_paciente = pacienteId;
      // Remover examesNecessarios do data antes de salvar (não faz parte do schema do protocolo)
      const { examesNecessarios: _, ...dadosProtocolo } = data;
      
      // Usar protocoloId do estado se disponível, caso contrário usar initialData
      const currentProtocoloId = protocoloId || initialData?.id;
      
      console.log('💾 [ProntuarioRetinologo] Salvando protocolo:', {
        currentProtocoloId,
        protocoloId,
        initialDataId: initialData?.id,
        atendimentoId,
        pacienteId: pacienteId
      });
      
      const response = await saveFormProtocolo({
        endpoint: '/api/protocolo-prontuario-retinologo',
        data: dadosProtocolo,
        atendimentoId: atendimentoId!,
        pacienteId: pacienteId,
        empresaId: empresaId,
        protocoloId: currentProtocoloId,
        statusAtendimento,
        formName: 'Prontuário Retinólogo'
      });

      // Retornar response junto com examesNecessarios para usar no onSuccess
      return { response, examesNecessarios };
    },
    onSuccess: async ({ response, examesNecessarios }) => {
      // Atualizar o formData com os dados retornados do backend
      let savedProtocoloId: number | undefined;
      
      console.log('✅ [ProntuarioRetinologo] Resposta do salvamento:', {
        response,
        hasResult: !!response?.result,
        resultId: response?.result?.id
      });
      
      if (response?.result) {
        savedProtocoloId = response.result.id;
        
        // IMPORTANTE: Sempre atualizar o protocoloId quando receber uma resposta
        console.log('📝 [ProntuarioRetinologo] Atualizando protocoloId para:', savedProtocoloId);
        setProtocoloId(savedProtocoloId);
        
        // Atualizar formData com os dados retornados (incluindo o ID)
        const updatedData = getInitialData(response.result);
        // Garantir que o ID esteja no formData
        const formDataWithId = {
          ...updatedData,
          id: savedProtocoloId
        };
        setFormData(formDataWithId);
        
        // Atualizar status se presente
        if (response.result.status_atendimento) {
          setStatusAtendimento(response.result.status_atendimento);
        }
        
        // Atualizar o componente pai através do onDataChange
        // Isso garante que na próxima vez que salvar, o ID correto será usado
        if (onDataChange) {
          onDataChange({
            ...formDataWithId,
            id: savedProtocoloId,
            id_paciente: response.result.id_paciente,
            id_atendimento: response.result.id_atendimento,
            id_empresa: response.result.id_empresa
          } as any);
        }
        
        console.log('✅ [ProntuarioRetinologo] Estado atualizado com sucesso:', {
          protocoloId: savedProtocoloId,
          formDataId: formDataWithId.id
        });
      } else if (statusAtendimento) {
        // Fallback: apenas atualizar status se não houver resposta completa
        console.warn('⚠️ [ProntuarioRetinologo] Resposta sem result, usando fallback');
        setFormData((prevData) => ({
          ...prevData,
          status_atendimento: statusAtendimento
        }));
        savedProtocoloId = initialData?.id || protocoloId;
        if (savedProtocoloId) {
          setProtocoloId(savedProtocoloId);
        }
      } else {
        console.error('❌ [ProntuarioRetinologo] Resposta inválida do servidor:', response);
      }

      // Salvar exames solicitados via API
      if (savedProtocoloId && examesNecessarios && examesNecessarios.length > 0) {
        try {
          console.log('Salvando exames solicitados:', {
            id_protocolo_prontuario_retinologo: savedProtocoloId,
            nomesExames: examesNecessarios
          });

          const result = await api.post('/api/exames_solicitados/protocolo', {
            id_protocolo_prontuario_retinologo: savedProtocoloId,
            nomesExames: examesNecessarios
          });

          console.log('Exames solicitados salvos com sucesso:', result);
        } catch (error: any) {
          console.error('Erro ao salvar exames solicitados:', error);
          toast.warning("Protocolo salvo, mas houve erro ao salvar os exames solicitados.");
        }
      } else {
        // Se não há exames, remover todos os exames do protocolo
        if (savedProtocoloId && (!examesNecessarios || examesNecessarios.length === 0)) {
          try {
            await api.post('/api/exames_solicitados/protocolo', {
              id_protocolo_prontuario_retinologo: savedProtocoloId,
              nomesExames: []
            });
          } catch (error: any) {
            console.error('Erro ao limpar exames solicitados:', error);
          }
        }
        console.log('Nenhum exame para salvar:', {
          savedProtocoloId,
          examesNecessarios,
          hasExames: examesNecessarios && examesNecessarios.length > 0
        });
      }

      // Disparar evento para atualizar painel de exames e documentos
      if (atendimentoId) {
        const event = new CustomEvent('examesAtualizados', {
          detail: { atendimentoId }
        });
        window.dispatchEvent(event);
      }
      
      toast.success("Formulário salvo com sucesso!");
    },
    onError: (error: any) => {
      console.error('Erro ao salvar prontuário retinólogo:', error);
      toast.error("Não foi possível salvar o formulário.");
    }
  });

  const handleInputChange = useCallback((field: string, value: any) => {
    setFormData((prevData) => {
      const newData = { ...prevData, [field]: value };
      const dataWithStatus = statusAtendimento 
        ? { ...newData, status_atendimento: statusAtendimento } as any
        : newData;
      onDataChange?.(dataWithStatus);
      return newData;
    });
  }, [onDataChange, statusAtendimento]);

  const handleSave = useCallback(async () => {
    if (!atendimentoId) {
      toast.error("ID do atendimento não fornecido.");
      return;
    }

    // Definir campos obrigatórios para Prontuário Retinólogo
    // Nota: Segundo o schema do backend, todos os campos são opcionais
    const camposObrigatorios: CampoObrigatorio[] = [];

    const dataToSave = statusAtendimento 
      ? { ...formData, status_atendimento: statusAtendimento } as any
      : formData;

    // Validar campos obrigatórios
    if (!validarCamposObrigatorios(dataToSave, camposObrigatorios)) {
      return;
    }

    try {
      console.log('Salvando prontuário retinólogo:', { endpoint: '/api/protocolo-prontuario-retinologo', data: dataToSave, atendimentoId });
      await saveMutation.mutateAsync(dataToSave);
      console.log('Prontuário retinólogo salvo com sucesso');
    } catch (error) {
      console.error('Erro ao salvar prontuário retinólogo:', error);
      toast.error("Não foi possível salvar o formulário. Verifique o console para mais detalhes.");
    }
  }, [atendimentoId, formData, statusAtendimento, saveMutation]);

  // Função para visualizar o PDF
  const handleVisualizar = useCallback(async () => {
    const currentProtocoloId = protocoloId || initialData?.id;
    
    if (!currentProtocoloId) {
      toast.warning("Salve o formulário antes de visualizar.");
      return;
    }

    try {
      const BASE_URL = import.meta.env.VITE_API_BASE_URL;
      const url = `${BASE_URL}/api/protocolo-prontuario-retinologo/${currentProtocoloId}/imprimir`;
      const blob = await buscarPdfParaVisualizacao({
        atendimentoId: atendimentoId,
        protocoloId: currentProtocoloId,
        tipoProtocolo: "prontuario_retinologo",
        fallbackUrl: url
      });

      const urlBlob = window.URL.createObjectURL(blob);
      setPdfUrl(urlBlob);
      setVisualizadorAberto(true);
    } catch (error: any) {
      console.error('Erro ao gerar PDF:', error);
      toast.error(error?.message || "Não foi possível gerar o PDF.");
    }
  }, [protocoloId, initialData?.id, atendimentoId]);

  const fecharVisualizador = () => {
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    setVisualizadorAberto(false);
    setPdfUrl(null);
  };

  const handleSwitchChange = useCallback((field: string, checked: boolean) => {
    handleInputChange(field, checked);
  }, [handleInputChange]);

  const handleOpenExamDialog = () => {
    setTempSelectedExams(formData.outrosExamesRealizados);
    setIsExamDialogOpen(true);
  };

  const handleConfirmExams = () => {
    handleInputChange('outrosExamesRealizados', tempSelectedExams);
    setIsExamDialogOpen(false);
  };

  const availableExamsNames = availableExams.map(exam => exam.nome);

  return (
    <div className="space-y-6">
      <div className="flex justify-end gap-2 mb-4">
        <Button onClick={handleSave} disabled={saveMutation.isPending}>
          <Save className="h-4 w-4 mr-2" />
          {saveMutation.isPending 
            ? (protocoloId || initialData?.id ? 'Atualizando...' : 'Salvando...') 
            : (protocoloId || initialData?.id ? 'Atualizar' : 'Salvar')}
        </Button>
        <Button 
          variant="outline" 
          onClick={handleVisualizar}
          disabled={!initialData?.id}
        >
          <Eye className="h-4 w-4 mr-2" />
          Visualizar Documento
        </Button>
      </div>
      <StatusSelector 
        atendimentoId={atendimentoId} 
        formName="Prontuário Retinólogo"
        value={formData.status_atendimento}
        onStatusSelect={(status) => {
          setStatusAtendimento(status || null);
          handleInputChange('status_atendimento', status || '');
        }}
      />
      {/* Motivo da consulta */}
      <div>
        <Label>Motivo da consulta</Label>
        <Textarea 
          placeholder="Descreva o motivo da consulta..." 
          rows={3} 
          value={formData.motivoConsulta}
          onChange={(e) => handleInputChange('motivoConsulta', e.target.value)}
        />
      </div>

      {/* Card História da Doença Atual */}
      <Card>
        <CardHeader>
          <CardTitle>História da Doença Atual</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Data Início Sintomas</Label>
            <Input 
              type="date"
              value={formData.dataInicioSintomas 
                ? (typeof formData.dataInicioSintomas === 'string' 
                    ? formData.dataInicioSintomas 
                    : new Date(formData.dataInicioSintomas).toISOString().split('T')[0])
                : ''}
              onChange={(e) => handleInputChange('dataInicioSintomas', e.target.value)}
            />
          </div>

          <div>
            <Label>Natureza dos sintomas</Label>
            <Textarea 
              placeholder="Descreva a natureza dos sintomas..." 
              rows={3} 
              value={formData.naturezaSintomas}
              onChange={(e) => handleInputChange('naturezaSintomas', e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label>Perda visual</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch 
                checked={formData.perdaVisual}
                onCheckedChange={(checked) => handleSwitchChange('perdaVisual', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Label>Visão borrada</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Não</span>
              <Switch 
                checked={formData.visaoBorrada}
                onCheckedChange={(checked) => handleSwitchChange('visaoBorrada', checked)}
              />
              <span className="text-sm text-muted-foreground">Sim</span>
            </div>
          </div>

          <div>
            <Label>Fotopsias</Label>
            <Textarea 
              placeholder="Descreva as fotopsias..." 
              rows={2} 
              value={formData.fotopsias}
              onChange={(e) => handleInputChange('fotopsias', e.target.value)}
            />
          </div>

          <div>
            <Label>Moscas volantes</Label>
            <Textarea 
              placeholder="Descreva as moscas volantes..." 
              rows={2} 
              value={formData.moscasVolantes}
              onChange={(e) => handleInputChange('moscasVolantes', e.target.value)}
            />
          </div>

          <div>
            <Label>Dor ocular</Label>
            <Textarea 
              placeholder="Descreva a dor ocular..." 
              rows={2} 
              value={formData.dorOcular}
              onChange={(e) => handleInputChange('dorOcular', e.target.value)}
            />
          </div>

          <div>
            <Label>Evolução e duração</Label>
            <Textarea 
              placeholder="Descreva a evolução e duração..." 
              rows={2} 
              value={formData.evolucaoDuracao}
              onChange={(e) => handleInputChange('evolucaoDuracao', e.target.value)}
            />
          </div>

          <div>
            <Label>Fatores que agravam ou aliviam</Label>
            <Textarea 
              placeholder="Descreva os fatores que agravam ou aliviam..." 
              rows={2} 
              value={formData.fatoresAgravamOuAliviam}
              onChange={(e) => handleInputChange('fatoresAgravamOuAliviam', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Card História médica e Pregressa Ocular */}
      <Card>
        <CardHeader>
          <CardTitle>História médica e Pregressa Ocular</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-2 block">Doenças sistêmicas associadas:</Label>
            
            <div className="flex items-center justify-between mb-3">
              <Label className="font-normal">Diabetes</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Não</span>
                <Switch 
                  checked={formData.diabetes}
                  onCheckedChange={(checked) => handleSwitchChange('diabetes', checked)}
                />
                <span className="text-sm text-muted-foreground">Sim</span>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <Label className="font-normal">Hipertensão</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Não</span>
                <Switch 
                  checked={formData.hipertensao}
                  onCheckedChange={(checked) => handleSwitchChange('hipertensao', checked)}
                />
                <span className="text-sm text-muted-foreground">Sim</span>
              </div>
            </div>
          </div>

          <div>
            <Label>Medicamentos de uso contínuo ou controlado?</Label>
            <Textarea 
              placeholder="Liste os medicamentos..." 
              rows={3} 
              value={formData.medicamentosUsoContinuo}
              onChange={(e) => handleInputChange('medicamentosUsoContinuo', e.target.value)}
            />
          </div>

          <div>
            <Label>Cirurgias oculares prévias</Label>
            <Textarea 
              placeholder="Descreva as cirurgias oculares prévias..." 
              rows={2} 
              value={formData.cirurgiasOcularesPrevias}
              onChange={(e) => handleInputChange('cirurgiasOcularesPrevias', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Uso de lentes corretivas</Label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Não</span>
                <Switch 
                  checked={formData.usoLentesCorretivas}
                  onCheckedChange={(checked) => handleSwitchChange('usoLentesCorretivas', checked)}
                />
                <span className="text-sm text-muted-foreground">Sim</span>
              </div>
            </div>
            {formData.usoLentesCorretivas && (
              <div>
                <Label>Grau</Label>
                <Input 
                  placeholder="Grau" 
                  value={formData.grauLentes}
                  onChange={(e) => handleInputChange('grauLentes', e.target.value)}
                />
              </div>
            )}
          </div>

          <div>
            <Label>Exames de retina anteriores (Retinografia, OCT, angiografia)</Label>
            <Textarea 
              placeholder="Descreva os exames de retina anteriores..." 
              rows={3} 
              value={formData.examesRetinaAnteriores}
              onChange={(e) => handleInputChange('examesRetinaAnteriores', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Card Exame Físico/Oftalmológico */}
      <Card>
        <CardHeader>
          <CardTitle>Exame Físico/Oftalmológico</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Acuidade visual (OD) - Com correção</Label>
              <Input 
                placeholder="Ex: 20/20" 
                maxLength={20}
                value={formData.acuidadeVisualOdComCorrecao}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 20)
                  handleInputChange('acuidadeVisualOdComCorrecao', value)
                }}
              />
              {formData.acuidadeVisualOdComCorrecao && formData.acuidadeVisualOdComCorrecao.length >= 20 && (
                <p className="text-xs text-muted-foreground mt-1">Máximo de 20 caracteres</p>
              )}
            </div>
            <div>
              <Label>Acuidade visual (OD) - Sem correção</Label>
              <Input 
                placeholder="Ex: 20/25" 
                maxLength={20}
                value={formData.acuidadeVisualOdSemCorrecao}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 20)
                  handleInputChange('acuidadeVisualOdSemCorrecao', value)
                }}
              />
              {formData.acuidadeVisualOdSemCorrecao && formData.acuidadeVisualOdSemCorrecao.length >= 20 && (
                <p className="text-xs text-muted-foreground mt-1">Máximo de 20 caracteres</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Acuidade visual (OE) - Com correção</Label>
              <Input 
                placeholder="Ex: 20/20" 
                maxLength={20}
                value={formData.acuidadeVisualOeComCorrecao}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 20)
                  handleInputChange('acuidadeVisualOeComCorrecao', value)
                }}
              />
              {formData.acuidadeVisualOeComCorrecao && formData.acuidadeVisualOeComCorrecao.length >= 20 && (
                <p className="text-xs text-muted-foreground mt-1">Máximo de 20 caracteres</p>
              )}
            </div>
            <div>
              <Label>Acuidade visual (OE) - Sem correção</Label>
              <Input 
                placeholder="Ex: 20/25" 
                maxLength={20}
                value={formData.acuidadeVisualOeSemCorrecao}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 20)
                  handleInputChange('acuidadeVisualOeSemCorrecao', value)
                }}
              />
              {formData.acuidadeVisualOeSemCorrecao && formData.acuidadeVisualOeSemCorrecao.length >= 20 && (
                <p className="text-xs text-muted-foreground mt-1">Máximo de 20 caracteres</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Pressão intraocular OD</Label>
              <Input 
                placeholder="Ex: 14 mmHg" 
                maxLength={20}
                value={formData.pressaoIntraocularOd}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 20)
                  handleInputChange('pressaoIntraocularOd', value)
                }}
              />
              {formData.pressaoIntraocularOd && formData.pressaoIntraocularOd.length >= 20 && (
                <p className="text-xs text-muted-foreground mt-1">Máximo de 20 caracteres</p>
              )}
            </div>
            <div>
              <Label>Pressão intraocular OE</Label>
              <Input 
                placeholder="Ex: 15 mmHg" 
                maxLength={20}
                value={formData.pressaoIntraocularOe}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 20)
                  handleInputChange('pressaoIntraocularOe', value)
                }}
              />
              {formData.pressaoIntraocularOe && formData.pressaoIntraocularOe.length >= 20 && (
                <p className="text-xs text-muted-foreground mt-1">Máximo de 20 caracteres</p>
              )}
            </div>
          </div>

          <div>
            <Label>Achados no exame de fundo de olho</Label>
            <Textarea 
              placeholder="Descreva os achados..." 
              rows={4} 
              value={formData.achadosFundoOlho}
              onChange={(e) => handleInputChange('achadosFundoOlho', e.target.value)}
            />
          </div>

          <div>
            <Label>Outros exames realizados no atendimento</Label>
            <div className="space-y-2">
              {formData.outrosExamesRealizados.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.outrosExamesRealizados.map((exam, index) => (
                    <div 
                      key={index} 
                      className="px-3 py-1 bg-secondary text-secondary-foreground rounded-md text-sm flex items-center gap-2"
                    >
                      <span>{exam}</span>
                      <button
                        type="button"
                        onClick={() => {
                          const updated = formData.outrosExamesRealizados.filter((_, i) => i !== index);
                          handleInputChange('outrosExamesRealizados', updated);
                        }}
                        className="text-xs hover:text-destructive"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <Button 
                type="button"
                variant="outline" 
                onClick={handleOpenExamDialog}
              >
                Selecionar Exames
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Card Plano Terapêutico e Conduta */}
      <Card>
        <CardHeader>
          <CardTitle>Plano Terapêutico e Conduta</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label>Tratamento indicado</Label>
            <Textarea 
              placeholder="Descreva o tratamento indicado..." 
              rows={4} 
              value={formData.tratamentoIndicado}
              onChange={(e) => handleInputChange('tratamentoIndicado', e.target.value)}
            />
          </div>

          <div>
            <Label className="mb-2 block">Necessidade de exame</Label>
            <ExamesSolicitacao
              initialExams={Array.isArray(formData.examesNecessarios) ? formData.examesNecessarios : []}
              availableExams={availableExamsNames}
              onExamsChange={(exams) => handleInputChange('examesNecessarios', exams)}
              protocoloId={protocoloId}
              onSaveExames={async (exames: string[]) => {
                if (!protocoloId) {
                  throw new Error('ID do protocolo não encontrado. Salve o formulário primeiro.');
                }
                
                console.log('Salvando exames ao incluir:', {
                  id_protocolo_prontuario_retinologo: protocoloId,
                  nomesExames: exames
                });

                await api.post('/api/exames_solicitados/protocolo', {
                  id_protocolo_prontuario_retinologo: protocoloId,
                  nomesExames: exames
                });

                // Atualizar o formData com os exames salvos
                setFormData((prevData) => ({
                  ...prevData,
                  examesNecessarios: exames
                }));
                setQuantidadeExames(exames.length);
              }}
            />
          </div>

          <div>
            <Label>Retorno/seguimento</Label>
            <Textarea 
              placeholder="Descreva o retorno e seguimento..." 
              rows={3} 
              value={formData.retornoSeguimento}
              onChange={(e) => handleInputChange('retornoSeguimento', e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Modal de Seleção de Exames Realizados */}
      <Dialog open={isExamDialogOpen} onOpenChange={setIsExamDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Selecionar Exames Realizados</DialogTitle>
          </DialogHeader>
          {loadingExams ? (
            <div className="py-8 text-center text-muted-foreground">
              Carregando exames...
            </div>
          ) : (
            <>
              <div className="space-y-3 max-h-[60vh] overflow-y-auto">
                {availableExams.map((exam) => (
                  <label
                    key={exam.id}
                    className="flex items-center gap-2 p-2 border rounded-md cursor-pointer hover:bg-accent/40 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={tempSelectedExams.includes(exam.nome)}
                      onChange={(e) => {
                        if (e.target.checked)
                          setTempSelectedExams((prev) => [...prev, exam.nome]);
                        else
                          setTempSelectedExams((prev) => prev.filter((x) => x !== exam.nome));
                      }}
                      className="w-4 h-4 accent-primary"
                    />
                    <span className="text-sm">{exam.nome}</span>
                  </label>
                ))}
                {availableExams.length === 0 && (
                  <div className="py-8 text-center text-muted-foreground">
                    Nenhum exame cadastrado
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setIsExamDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleConfirmExams}>
                  Confirmar
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <div className="mt-6 pt-6 border-t">
        <Anexos 
          protocoloId={protocoloId}
          quantidadeExames={quantidadeExames || (Array.isArray(formData.examesNecessarios) ? formData.examesNecessarios.length : 0)}
          endpointBase="/api/protocolo-prontuario-retinologo"
        />
      </div>
      {/* Visualizador de PDF */}
      {pdfUrl && (
        <VisualizadorArquivo
          arquivoUrl={pdfUrl}
          nomeArquivo={`prontuario_retinologo_${initialData?.id || 'novo'}.pdf`}
          tipoArquivo="pdf"
          open={visualizadorAberto}
          onClose={fecharVisualizador}
        />
      )}
    </div>
  );
};

export default ProntuarioRetinologo;
